/**
 * Impersonation Error Models
 *
 * Error types, codes, and classes for impersonation operations. Provides user-friendly error messages and structured
 * error handling.
 *
 * @packageDocumentation
 */

/**
 * Error codes for impersonation operations
 *
 * These codes map to specific error scenarios and provide consistent error handling across the impersonation lifecycle.
 */
export enum ImpersonationErrorCode {
  /** Admin lacks User.Simulate entitlement */
  MISSING_ENTITLEMENT = 'MISSING_ENTITLEMENT',

  /** Already impersonating (nested impersonation not allowed) */
  ALREADY_IMPERSONATING = 'ALREADY_IMPERSONATING',

  /** Target user not found or invalid */
  USER_NOT_FOUND = 'USER_NOT_FOUND',

  /** Backend API call failed (network, 500 error, etc.) */
  BACKEND_ERROR = 'BACKEND_ERROR',

  /** Not currently impersonating (can't end impersonation) */
  NOT_IMPERSONATING = 'NOT_IMPERSONATING',

  /** Admin's original privileges were revoked during impersonation */
  ADMIN_REVOKED = 'ADMIN_REVOKED',

  /** Admin's token expired during impersonation */
  ADMIN_TOKEN_EXPIRED = 'ADMIN_TOKEN_EXPIRED',

  /** Encryption/decryption failed */
  ENCRYPTION_ERROR = 'ENCRYPTION_ERROR',

  /** JWT token parsing failed */
  INVALID_TOKEN = 'INVALID_TOKEN',

  /** Session validation failed (backend rejected session) */
  SESSION_INVALID = 'SESSION_INVALID',

  /** Unknown error */
  UNKNOWN = 'UNKNOWN',
}

/** User-friendly error messages mapped to error codes */
export const ERROR_MESSAGES: Record<ImpersonationErrorCode, string> = {
  [ImpersonationErrorCode.MISSING_ENTITLEMENT]:
    'You do not have permission to impersonate users. User.Simulate entitlement is required.',

  [ImpersonationErrorCode.ALREADY_IMPERSONATING]:
    'You are already impersonating a user. Please end the current impersonation session before starting a new one.',

  [ImpersonationErrorCode.USER_NOT_FOUND]: 'The selected user could not be found or is invalid. Please try again.',

  [ImpersonationErrorCode.BACKEND_ERROR]:
    'Unable to complete impersonation due to a server error. Please try again later.',

  [ImpersonationErrorCode.NOT_IMPERSONATING]: 'You are not currently impersonating a user. There is no session to end.',

  [ImpersonationErrorCode.ADMIN_REVOKED]:
    'Your administrator privileges have been revoked. You will be logged out for security.',

  [ImpersonationErrorCode.ADMIN_TOKEN_EXPIRED]: 'Your session has expired. Please log in again to continue.',

  [ImpersonationErrorCode.ENCRYPTION_ERROR]:
    'Failed to secure your session data. Please contact support if this persists.',

  [ImpersonationErrorCode.INVALID_TOKEN]: 'Received an invalid authentication token. Please try logging in again.',

  [ImpersonationErrorCode.SESSION_INVALID]:
    'Your impersonation session is no longer valid. Please start a new impersonation.',

  [ImpersonationErrorCode.UNKNOWN]: 'An unexpected error occurred. Please try again or contact support.',
};

/**
 * Impersonation error class
 *
 * Extends Error with code and user-friendly message for impersonation failures. Use this class for all
 * impersonation-related errors to ensure consistent handling.
 *
 * @example
 *   ```typescript
 *   throw new ImpersonationError(
 *     ImpersonationErrorCode.MISSING_ENTITLEMENT,
 *     'Admin lacks User.Simulate'
 *   );
 *   ```;
 */
export class ImpersonationError extends Error {
  /** Error code for programmatic error handling */
  public readonly code: ImpersonationErrorCode;

  /** User-friendly error message (safe to display in UI) */
  public readonly userMessage: string;

  /** Original error (if this wraps another error) */
  public readonly cause?: Error;

  /**
   * Creates a new ImpersonationError
   *
   * @param code - Error code from ImpersonationErrorCode enum
   * @param message - Technical error message (for logging/debugging)
   * @param cause - Original error that caused this error (optional)
   */
  constructor(code: ImpersonationErrorCode, message?: string, cause?: Error) {
    super(message || ERROR_MESSAGES[code]);
    this.name = 'ImpersonationError';
    this.code = code;
    this.userMessage = ERROR_MESSAGES[code];
    this.cause = cause;

    // Maintains proper stack trace for where error was thrown (V8 engines only)
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, ImpersonationError);
    }
  }

  /** Converts error to JSON for logging */
  toJSON(): object {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      cause: this.cause?.message,
      stack: this.stack,
    };
  }
}

/** API error response structure from backend */
export interface ImpersonationApiError {
  /** Error message from backend */
  readonly message: string;

  /** Error code (if backend provides structured errors) */
  readonly code?: string;

  /** HTTP status code */
  readonly status: number;

  /** Additional error details */
  readonly details?: Record<string, unknown>;
}
